import React, { useEffect, useState } from "react";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Link, useParams } from "react-router-dom";

const API_TOKEN = process.env.REACT_APP_API_TOKEN;

function OpenStudio() {
  const { slug } = useParams();

  const [studio, setStudio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [newsletterOptin, setNewsletterOptin] = useState(false);
  const [website, setWebsite] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [access, setAccess] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadStudio = async () => {
      setLoading(true);
      setLoadError("");
      setStudio(null);
      setAccess(null);

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_FOLDER}get_openstudio/?slug=${encodeURIComponent(slug)}`,
          { headers: { "X-API-TOKEN": API_TOKEN } }
        );

        if (cancelled) return;

        if (response.data && response.data.error) {
          setLoadError(response.data.error);
        } else {
          setStudio(response.data);
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError(error.message || "This Open Studio could not be loaded.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadStudio();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const submitAccess = async (event) => {
    event.preventDefault();
    setFormError("");

    if (!firstName.trim() || !email.trim()) {
      setFormError("Enter your first name and email address.");
      return;
    }

    setSubmitting(true);

    try {
      const payload = new URLSearchParams();
      payload.append("slug", slug);
      payload.append("first_name", firstName.trim());
      payload.append("email", email.trim());
      payload.append("newsletter_optin", newsletterOptin ? "1" : "0");
      payload.append("website", website);

      const response = await axios.post(
        `${process.env.REACT_APP_API_FOLDER}register_openstudio/`,
        payload,
        {
          headers: {
            "X-API-TOKEN": API_TOKEN,
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      if (!response.data || response.data.success !== true || !response.data.access) {
        setFormError(
          (response.data && response.data.error) ||
            "We couldn't open the session. Please try again."
        );
        return;
      }

      setAccess(response.data);
    } catch (error) {
      const responseMessage =
        error.response &&
        error.response.data &&
        error.response.data.error;

      setFormError(
        responseMessage || error.message || "We couldn't open the session. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <section className="openstudio-page section-bottom-pad">
        <div className="package-details-wrapper pt-110">
          <div className="container">
            <Skeleton height={60} />
            <div className="mt-30">
              <Skeleton height={440} />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (loadError || !studio) {
    return (
      <section className="openstudio-page section-bottom-pad">
        <div className="package-details-wrapper pt-110">
          <div className="container">
            <h2>Open Studio unavailable</h2>
            <p>{loadError || "This Open Studio could not be found."}</p>
            <Link to="/openstudios">← Open Studios</Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="openstudio-page section-bottom-pad">
      <div className="package-details-wrapper pt-110">
        <div className="container">
          <Link
            className="openstudio-back-link"
            to="/openstudios"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <i className="bi bi-arrow-left" aria-hidden="true" /> Open Studios
          </Link>

          <div className="openstudio-hero">
            <div className="openstudio-hero-image">
              {studio.image ? (
                <img src={studio.image} alt={`${studio.title} with ${studio.presenter}`} />
              ) : (
                <div className="openstudio-hero-placeholder" aria-hidden="true" />
              )}
            </div>

            <div className="openstudio-hero-copy">
              <p className="openstudio-kicker">CAMP Open Studio</p>
              <h1>{studio.title}</h1>
              <p className="openstudio-presenter">with {studio.presenter}</p>

              <div className="openstudio-schedule">
                <div>
                  <i className="bi bi-calendar-event" aria-hidden="true" />
                  {studio.date_label}
                </div>
                <div>
                  <i className="bi bi-clock" aria-hidden="true" />
                  {studio.time_label} {studio.timezone_label}
                </div>
                <div>
                  <i className="bi bi-camera-video" aria-hidden="true" />
                  Online · free
                </div>
              </div>

              <div className="openstudio-description">{studio.description}</div>

              {studio.registration_open && !access && (
                <div className="openstudio-access-card">
                  <h2>Join Open Studio</h2>
                  <p>
                    Enter your details and we'll open the Zoom link immediately.
                    No Zoom registration or account is required.
                  </p>

                  <form onSubmit={submitAccess}>
                    <div className="openstudio-form-row">
                      <div>
                        <label htmlFor="openstudio-first-name">First name</label>
                        <input
                          id="openstudio-first-name"
                          type="text"
                          autoComplete="given-name"
                          maxLength="100"
                          required
                          value={firstName}
                          onChange={(event) => setFirstName(event.target.value)}
                        />
                      </div>

                      <div>
                        <label htmlFor="openstudio-email">Email</label>
                        <input
                          id="openstudio-email"
                          type="email"
                          autoComplete="email"
                          maxLength="255"
                          required
                          value={email}
                          onChange={(event) => setEmail(event.target.value)}
                        />
                      </div>
                    </div>

                    <div className="openstudio-honeypot" aria-hidden="true">
                      <label htmlFor="openstudio-website">Website</label>
                      <input
                        id="openstudio-website"
                        type="text"
                        tabIndex="-1"
                        autoComplete="off"
                        value={website}
                        onChange={(event) => setWebsite(event.target.value)}
                      />
                    </div>

                    <label className="openstudio-consent">
                      <input
                        type="checkbox"
                        checked={newsletterOptin}
                        onChange={(event) => setNewsletterOptin(event.target.checked)}
                      />
                      <span>
                        Send me future CAMP Open Studios, workshops, Field Notes and occasional CAMP news.
                      </span>
                    </label>

                    <p className="openstudio-privacy">
                      The email address is used to administer this Open Studio.
                      Mailing-list signup is optional and requires email confirmation.{" "}
                      <Link to="/privacy">Privacy policy</Link>.
                    </p>

                    {formError && (
                      <div className="openstudio-form-error" role="alert">
                        {formError}
                      </div>
                    )}

                    <button type="submit" disabled={submitting}>
                      {submitting ? "Opening…" : "Get access"}
                    </button>
                  </form>
                </div>
              )}

              {access && (
                <div className="openstudio-access-card openstudio-access-success">
                  <h2>You're in</h2>
                  <p>Your Open Studio access is ready.</p>

                  <a
                    className="openstudio-join-button"
                    href={access.zoom_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Join Open Studio on Zoom
                    <i className="bx bxs-right-arrow-alt" aria-hidden="true" />
                  </a>

                  {access.newsletter_message && (
                    <p className="openstudio-newsletter-message">
                      {access.newsletter_message}
                    </p>
                  )}
                </div>
              )}

              {!studio.registration_open && studio.replay_url && (
                <div className="openstudio-access-card">
                  <h2>Watch the replay</h2>
                  <a
                    className="openstudio-join-button"
                    href={studio.replay_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Watch Open Studio
                    <i className="bx bxs-right-arrow-alt" aria-hidden="true" />
                  </a>
                </div>
              )}

              {!studio.registration_open && !studio.replay_url && (
                <div className="openstudio-ended">
                  This Open Studio has ended.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default OpenStudio;
